import { Star, StarHalf, Info, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/hooks/use-toast";
import { PurchaseConfirmationModal } from "@/components/cart/PurchaseConfirmationModal";
import { ProductDetailsModal } from "./ProductDetailsModal";
import { useState } from "react";

interface ServiceCardProps {
  id: string;
  title: string;
  description: string;
  price: string;
  rating: number;
  reviewCount: number;
  imageUrl: string;
  badge?: string;
  badgeVariant?: "default" | "secondary" | "destructive";
}

export function ServiceCard({
  id,
  title,
  description,
  price,
  rating,
  reviewCount,
  imageUrl,
  badge,
  badgeVariant = "default"
}: ServiceCardProps) {
  const { addItem } = useCart();
  const { toast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const handleAddToCart = () => {
    addItem({
      id,
      title,
      description,
      price,
      imageUrl
    });
    
    toast({
      title: "Produto adicionado!",
      description: `${title} foi adicionado ao seu carrinho.`,
    });
    
    setShowModal(true);
  };
  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />);
    }

    if (hasHalfStar) {
      stars.push(<StarHalf key="half" className="w-4 h-4 fill-yellow-400 text-yellow-400" />);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="w-4 h-4 text-gray-300" />);
    }

    return stars;
  };

  return (
    <div className="bg-card rounded-lg shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md hover:-translate-y-1 group">
      <div className="h-48 relative overflow-hidden">
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {badge && (
          <Badge
            variant={badgeVariant === "default" ? "default" : badgeVariant === "secondary" ? "secondary" : "destructive"}
            className="absolute top-3 right-3"
          >
            {badge}
          </Badge>
        )}
        <Button
          variant="secondary"
          size="sm"
          className="absolute top-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-background/80 backdrop-blur-sm hover:bg-background/90"
          onClick={() => setShowDetailsModal(true)}
        >
          <Info className="w-4 h-4 mr-1" />
          Detalhes
        </Button>
      </div>
      
      <div className="p-4">
        <div className="flex items-center mb-2">
          <div className="flex">
            {renderStars(rating)}
          </div>
          <span className="text-xs text-muted-foreground ml-2">
            ({reviewCount} avaliações)
          </span>
        </div>
        
        <h3 className="font-semibold text-lg mb-1 text-card-foreground">
          {title}
        </h3>
        
        <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
          {description}
        </p>
        
        <div className="flex items-center justify-between gap-2">
          <span className="text-primary font-bold text-lg">
            {price}
          </span>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowDetailsModal(true)}
              className="hover-scale"
            >
              <Info className="w-4 h-4" />
            </Button>
            <Button 
              variant="default" 
              size="sm" 
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
              onClick={handleAddToCart}
            >
              <ShoppingCart className="w-4 h-4 mr-1" />
              Comprar
            </Button>
          </div>
        </div>
      </div>
      
      <ProductDetailsModal
        isOpen={showDetailsModal}
        onOpenChange={setShowDetailsModal}
        product={{
          id,
          title,
          description,
          price,
          rating,
          reviewCount,
          imageUrl,
          badge,
          badgeVariant
        }}
      />
      
      <PurchaseConfirmationModal
        isOpen={showModal}
        onOpenChange={setShowModal}
        productTitle={title}
        productPrice={price}
      />
    </div>
  );
}